/**
 * AdminTabs - Standardized tabs component for admin pages
 * Provides consistent tab styling and behavior
 */

import React from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import { PAGE_SPACING as ADMIN_SPACING } from '../ui/PageLayout';

/**
 * Tab definition
 */
export interface AdminTab {
  label: string;
  icon?: React.ReactElement;
  content: React.ReactNode;
  disabled?: boolean;
}

/**
 * Admin tabs props
 */
export interface AdminTabsProps {
  tabs: AdminTab[];
  value?: number;
  onChange?: (newValue: number) => void;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  orientation?: 'horizontal' | 'vertical';
}

/**
 * Tab panel component
 */
interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ pt: ADMIN_SPACING.tabContentPadding }}>
          {children}
        </Box>
      )}
    </div>
  );
};

/**
 * Standardized admin tabs component
 */
export const AdminTabs: React.FC<AdminTabsProps> = ({
  tabs,
  value: controlledValue,
  onChange,
  variant = 'standard',
  orientation = 'horizontal',
}) => {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(0);

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : uncontrolledValue;

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setUncontrolledValue(newValue);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: orientation === 'vertical' ? 'row' : 'column',
        gap: orientation === 'vertical' ? 3 : 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderBottom: orientation === 'horizontal' ? 1 : 0,
          borderRight: orientation === 'vertical' ? 1 : 0,
          borderColor: 'divider',
          mb: orientation === 'horizontal' ? 0 : 0,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant={variant}
          orientation={orientation}
          scrollButtons="auto"
          aria-label="admin tabs"
          sx={{
            minHeight: orientation === 'vertical' ? 'auto' : undefined,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9375rem',
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              disabled={tab.disabled}
              id={`admin-tab-${index}`}
              aria-controls={`admin-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ flex: 1 }}>
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {tab.content}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};
